import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

// Constants
import { routes, SKETCH_WEBSITE } from '@sketch/constants'
import { useFlag } from 'containers/flags'

// Context
import { useModalContext } from 'containers/ModalManager/ModalContext'

// Utils
import { dateFormat } from '@sketch/utils'

import {
  isWorkspaceSubscriptionActive,
  isWorkspaceSubscriptionTrialling,
  hasPaymentDetails,
} from 'modules/workspace/utils'

// Hooks
import { useMembershipsPanel } from 'modules/workspace/components/MembershipsPanel/hooks'

// Components
import { Button, Link, pluralize } from '@sketch/components'
import Banner from 'components/Banner'
import Section from 'components/Section'

// Local Components
import {
  MembershipsPanel,
  MemberActions,
  WorkspaceSettingsSubscribePanel,
  WorkspaceSettingsDetailsPanel,
} from 'modules/workspace/components'

// Modals
import {
  AddSeatsModal,
  InviteMembersNotAllowedModal,
  InviteMembersModal,
  DeleteWorkspaceModal,
} from 'modules/workspace/modals'

// Styles
import {
  Paragraph,
  MembersTitleContainer,
  WorkspaceMembershipsLoading,
  StyledBanner,
  DeleteWorkspaceText,
} from './WorkspaceSettingsPeople.styles'

// GQL
// Usage of BillingStatus enum is needed to make the expected strings match the BE ones
// eslint-disable-next-line no-restricted-imports
import { BillingStatus, WorkspaceMembership } from '@sketch/gql-types/expansive'
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
  PaymentDetailsFragment,
  useGetWorkspaceMembershipsQuery,
} from '@sketch/gql-types'

interface WorkspaceSettingsPeopleProps {
  workspaceId: string
  name: string
  avatar: string | undefined
  seats: BillingSeatsInfoFragment
  billingStatus: BillingStatus
  nextBillingCycleDate?: string
  customerId: string
  currentPlan?: BillingPlanFragment
  paymentDetails?: PaymentDetailsFragment
  isSsoEnabled: boolean
  subscriptionEnd?: string
  role: WorkspaceMembership['role']
  isEducationWorkspace?: boolean
  isIOS?: boolean
}

const WorkspaceSettingsPeople: React.FC<WorkspaceSettingsPeopleProps> = props => {
  const {
    workspaceId,
    customerId,
    name,
    avatar,
    seats,
    currentPlan,
    nextBillingCycleDate,
    billingStatus,
    paymentDetails,
    isSsoEnabled,
    subscriptionEnd,
    role,
    isEducationWorkspace,
    isIOS,
  } = props

  // TODO: Remove sketch-partners FF
  // https://github.com/sketch-hq/Cloud/issues/12404
  const isSketchPartnersEnabled = useFlag('sketch-partners')

  const { showModal, showRestrictedModal } = useModalContext()

  const isUserAdministrator = role === 'ADMIN'
  const isFinance = role === 'FINANCE'
  const isPartner = role === 'PARTNER'

  const scheduledSeats = seats.scheduledSeatsTotal ?? undefined

  const isOnTrial = isWorkspaceSubscriptionTrialling(billingStatus)
  const isSubscriptionActive = isWorkspaceSubscriptionActive(billingStatus)
  const customerHasPaymentDetails = hasPaymentDetails(paymentDetails)

  const onInviteMember = () => {
    if (isSsoEnabled) {
      showModal(InviteMembersNotAllowedModal, { workspaceId })
    } else {
      showRestrictedModal(InviteMembersModal, {
        customerId,
        isOnTrial,
        nextBillingCycleDate: nextBillingCycleDate!,
        plan: currentPlan!,
        seats,
        workspaceId,
        paymentDetails: paymentDetails!,
        isScheduledToBeCancelled: !!subscriptionEnd,
        isEducationWorkspace,
        isPartneredWorkspace,
        partner,
        isIOS,
      })
    }
  }

  // Workspace memberships
  const {
    data: membershipsData,
    loading: isLoadingMembership,
  } = useMembershipsPanel(workspaceId)
  const membershipsCount =
    membershipsData?.workspace.memberships.totalMemberships?.meta.totalCount ||
    undefined

  // Since Workspace memberships are layy loaded, explicitly filter by PARTNER
  // to make sure this membership is verified
  const {
    data: partnersData,
    loading: isLoadingPartner,
  } = useGetWorkspaceMembershipsQuery({
    variables: {
      workspaceId,
      role: 'PARTNER',
    },
    skip: !isSketchPartnersEnabled,
  })

  const workspaceHasPartner =
    partnersData?.workspace.memberships.list?.entries.length

  const isPartneredWorkspace =
    (workspaceHasPartner &&
      partnersData?.workspace.memberships.list?.entries[0].acceptedAt !=
        null) ||
    false

  const partner = workspaceHasPartner
    ? partnersData?.workspace.memberships.list?.entries[0].user
    : null

  const getMembersTitleCopy = () => (
    <MembersTitleContainer>
      {isLoadingMembership ? (
        <WorkspaceMembershipsLoading size="24px" />
      ) : (
        membershipsCount
      )}{' '}
      {pluralize('Person', 'People', membershipsCount || 1)} in {name}
    </MembersTitleContainer>
  )

  const getMembersTabCopy = () => {
    if (!isUserAdministrator && !isFinance && !isPartner) {
      return
    }

    const availableSeatsCopy = ` of which ${seats.availableSeats} ${pluralize(
      'is',
      'are',
      seats.availableSeats
    )} not in use.`
    const editorSeatsCountCopy = `${seats.currentSeatsTotal} Editor ${pluralize(
      'Seat',
      'Seats',
      seats.currentSeatsTotal
    )}`

    if (isOnTrial) {
      return (
        <Paragraph data-testid="members-tab-copy">
          After your trial, you will begin paying for{' '}
          <b>{editorSeatsCountCopy}</b>
          {seats.availableSeats > 0 ? availableSeatsCopy : null}
        </Paragraph>
      )
    } else {
      return (
        <Paragraph data-testid="members-tab-copy">
          You currently {isEducationWorkspace ? 'have' : 'pay for'}{' '}
          <b>{editorSeatsCountCopy}</b>
          {seats.availableSeats > 0 && ','} <br />
          {seats.availableSeats > 0 ? availableSeatsCopy : null}
        </Paragraph>
      )
    }
  }

  const renderMemberActions = () => {
    // When the subscription is managed by a partner, only render the
    // "Add Seats" buttons for partners
    // When there's no Partner, only render the buttons for Admin or Finance
    // users
    const canRenderButtons =
      !isLoadingPartner &&
      isSubscriptionActive &&
      (isPartner || isFinance || isUserAdministrator)

    if (!canRenderButtons) return

    const actions = []

    // Education Workspaces, Partnered Workspaces (if the current user is not the Partner)
    // and iOS Subscription Workspaces don't have the Add Seat button

    const skipAddSeatsButton =
      isEducationWorkspace || (workspaceHasPartner && !isPartner) || isIOS

    // Add Seats / Manage Seats Button
    if (!skipAddSeatsButton) {
      actions.push(
        <Button
          key="seatsButton"
          onClick={() =>
            showModal(AddSeatsModal, {
              seats,
              plan: currentPlan!,
              workspaceId,
              nextBillingCycleDate,
              customerId,
              isOnTrial,
              paymentDetails: paymentDetails,
              isScheduledToBeCancelled: !!subscriptionEnd,
              variant: isPartner ? 'partner' : 'default',
            })
          }
          size="40"
          variant={isPartner ? 'primary' : 'secondary'}
        >
          {isPartner ? 'Manage Seats…' : 'Add Seats…'}
        </Button>
      )
    }

    // Invite Members Button
    if (!isFinance && !isPartner) {
      actions.push(
        <Button
          key="inviteButton"
          onClick={() => onInviteMember()}
          size="40"
          variant="primary"
        >
          Invite Members…
        </Button>
      )
    }

    return actions
  }

  const renderDeleteWorkspacePanel = isIOS || isEducationWorkspace
  const educationBannerExpireDate = nextBillingCycleDate ?? subscriptionEnd
  const isIOSSubscriptionIsActive = isIOS && isSubscriptionActive
  const showIOSBanner = isIOS && isSubscriptionActive
  const showSubscribePanel =
    isUserAdministrator &&
    !customerHasPaymentDetails &&
    !isEducationWorkspace &&
    !isIOS
  const showWorkspaceDetails =
    (!isPartner && !isUserAdministrator) || isEducationWorkspace

  const deleteWorkspaceText = isIOSSubscriptionIsActive ? (
    <DeleteWorkspaceText>
      To delete this Workspace and all documents, first cancel your iOS
      subscription in the App Store.
      <br />
      {
        // TODO: Update Learn More link when we have it
        // https://github.com/sketch-hq/Cloud/issues/14986
      }
      <Link href={`${SKETCH_WEBSITE}/missing/link`} external>
        Learn More
      </Link>
    </DeleteWorkspaceText>
  ) : (
    <>
      Delete the Workspace, including all documents and members it contains.
      Proceed with caution.
    </>
  )

  return (
    <>
      {isEducationWorkspace && (
        <StyledBanner type="information">
          Your Education Workspace is free until{' '}
          {dateFormat(new Date(educationBannerExpireDate!))}. <br />
          <Link href={`${SKETCH_WEBSITE}/support/contact/`} external>
            Get in touch
          </Link>{' '}
          if you have any questions or need for an extension.
        </StyledBanner>
      )}

      {showIOSBanner && (
        <StyledBanner type="information">
          Head over to the App Store to manage your iOS subscription and billing
          details.{' '}
          {
            // TODO: Update Learn More link when we have it
            // https://github.com/sketch-hq/Cloud/issues/14986
          }
          <Link href={`${SKETCH_WEBSITE}/missing/link`} external>
            Learn More
          </Link>
        </StyledBanner>
      )}

      {showWorkspaceDetails && (
        <Section title="Workspace">
          <WorkspaceSettingsDetailsPanel
            name={name}
            id={workspaceId}
            avatar={avatar}
            isUserAdministrator={isUserAdministrator}
          />
        </Section>
      )}

      {showSubscribePanel && (
        <WorkspaceSettingsSubscribePanel workspaceId={workspaceId} />
      )}
      <Section
        title={getMembersTitleCopy()}
        text={getMembersTabCopy()}
        action={renderMemberActions()}
        align={isPartner ? 'center' : 'bottom'}
      >
        {/* Banner warning the user that has scheduled seats */}
        {!!scheduledSeats && isUserAdministrator && (
          <>
            <br />
            <Banner type="warning">
              <b>Scheduled change!</b> You’ve scheduled to have {scheduledSeats}{' '}
              seats starting on{' '}
              {nextBillingCycleDate &&
                dateFormat(new Date(nextBillingCycleDate))}
              {'. '}
              <RouterLink
                to={routes.WORKSPACE_SETTINGS_BILLING.create({
                  workspaceId: workspaceId,
                })}
              >
                See Details
              </RouterLink>
            </Banner>
          </>
        )}

        {!isFinance && !isPartner && (
          <MembershipsPanel
            workspaceId={workspaceId}
            renderSecondaryActions={
              isUserAdministrator
                ? member => (
                    <MemberActions
                      workspaceName={name}
                      workspaceId={workspaceId}
                      member={member}
                      billingStatus={billingStatus}
                      customerId={customerId}
                      seats={seats}
                      plan={currentPlan!}
                      paymentDetails={paymentDetails}
                      isSsoEnabled={isSsoEnabled}
                      hasPartner={!!workspaceHasPartner}
                      isPartneredWorkspace={isPartneredWorkspace}
                      isEducationWorkspace={isEducationWorkspace}
                      isIOS={isIOS}
                    />
                  )
                : undefined
            }
          />
        )}
      </Section>

      {isSketchPartnersEnabled && isPartner && (
        <Section title="Leave Workspace">
          If you no longer want to manage “<b>{name}</b>” as a Sketch Partner,
          get in touch with their Workspace Admin.{' '}
          <Link
            external
            href="https://www.sketch.com/support/partners"
            isUnderlined
          >
            Learn more
          </Link>
        </Section>
      )}

      {/* "data-testid="danger-zone-panel"" is used to keep consistency with the older panels in tests */}
      {renderDeleteWorkspacePanel && (
        <Section
          data-testid="danger-zone-panel"
          title="Delete Workspace"
          text={deleteWorkspaceText}
          action={
            isIOSSubscriptionIsActive ? null : (
              <Button
                onClick={() =>
                  showModal(DeleteWorkspaceModal, { id: workspaceId, name })
                }
                size="32"
                variant="negative-secondary"
              >
                Delete Workspace…
              </Button>
            )
          }
        />
      )}
    </>
  )
}

export default WorkspaceSettingsPeople
